import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, Paging, Selection, Sorting, SearchPanel, HeaderFilter
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import API_URL from '../config';

// --- API URL 정의 ---
const MASTER_API_URL = `${API_URL}/api/oracle/shipping-master`;
const SUB_API_URL = `${API_URL}/api/oracle/shipping-sub`;
const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;

// --- 날짜 포맷팅 헬퍼 함수 ---
const formatDate = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");
  return `${y}${m}${d}`;
};

export default function ShippingMasterDetailPage({ user }) {
  // --- State 정의 ---
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // 데이터 State
  const [masterData, setMasterData] = useState([]);
  const [subData, setSubData] = useState([]);

  // 콤보박스 데이터 State
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  
  // 검색 조건 State
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  
  // UI 상태 State
  const [loading, setLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [error, setError] = useState("");
  const [isFirstLoad, setIsFirstLoad] = useState(true); // 첫 로드인지 판별하는 플래그

  const masterGridRef = useRef(null);

  // --- 데이터 조회 로직 (useEffect) ---
  useEffect(() => {
    const fetchCusts = async () => {
      if (!selectedFactory) return;
      try {
        const res = await axios.get(CUST_API_URL, { params: { factory: selectedFactory } });
        setCustList(res.data.data || []);
      } catch (err) {
        console.error("CUST 목록 조회 실패:", err);
      }
    };
    fetchCusts();
  }, [selectedFactory]);

  useEffect(() => {
    const fetchDevices = async () => {
      if (!selectedFactory || !selectedCust) {
        setDeviceList([]);
        setSelectedDevice('ALL');
        return;
      }
      try {
        const res = await axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } });
        const fetchedDevices = res.data.data || [];
        setDeviceList([{ CODE_CD: 'ALL' }, ...fetchedDevices]);
        setSelectedDevice('ALL');
      } catch (err) {
        console.error("DEVICE 목록 조회 실패:", err);
      }
    };
    fetchDevices();
  }, [selectedFactory, selectedCust]);

  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
    }
  }, [user, custList]);
  
  // --- 이벤트 핸들러 ---
  const handleSearchClick = async () => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setMasterData([]);
    setSubData([]);
    setIsFirstLoad(true); // 조회가 시작되면 첫 로드 플래그를 true로 설정

    const params = {
      factory: selectedFactory,
      startDate: formatDate(dateFrom),
      endDate: formatDate(dateTo),
      custCd: selectedCust,
      device: selectedDevice,
    };
    
    try {
      const res = await axios.get(MASTER_API_URL, { params });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
      setMasterData(res.data.data.map((row, idx) => ({ ...row, id: idx })));
    } catch (e) {
      setError(`마스터 조회 오류: ${e.response?.data?.retMsg || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFocusedRowChanged = async (e) => {
    if (!e.row || !e.row.data) {
      setSubData([]);
      return;
    }
    const { LOT_ID, FACTORY } = e.row.data;
    if (!LOT_ID || !FACTORY) return;

    setSubLoading(true);
    setSubData([]);
    
    const params = { factory: FACTORY, lotId: LOT_ID };
    try {
      const res = await axios.get(SUB_API_URL, { params });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
      setSubData(res.data.data.map((row, idx) => ({ ...row, id: idx })));
    } catch (e) {
      notify(`서브 데이터 조회 오류: ${e.response?.data?.retMsg || e.message}`, 'error');
      setSubData([]);
    } finally {
      setSubLoading(false);
    }
  };
  
  // ★★★ [핵심 수정] 그리드 렌더링 완료 후 첫 행 포커스 ★★★
  const handleMasterGridContentReady = (e) => {
    // 첫 로드 플래그가 true이고, 데이터가 있을 때만 실행
    if (isFirstLoad && e.component.totalCount() > 0) {
      e.component.option('focusedRowIndex', 0);
      setIsFirstLoad(false); // 실행 후 플래그를 false로 바꿔 다시 실행되지 않도록 함
    }
  };

  return (
    <div style={{ padding: 20, height: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px', flexShrink: 0 }}>
        <h3 style={{ marginBottom: '20px' }}>Shipping Lot (Master-Detail)</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" width={180} />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" width={180} />
          <SelectBox items={['FT01', 'PT01']} value={selectedFactory} onValueChanged={e => setSelectedFactory(e.value)} label="FACTORY" width={150} />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} disabled={user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" width={220} />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="DEVICE" width={220} />
          <Button text="Search" type="default" icon="search" onClick={handleSearchClick} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>
      
      {/* 2. 마스터-디테일 레이아웃 컨테이너 */}
      <div style={{ flex: 1, display: 'flex', gap: '20px', minHeight: 0 }}>
        {/* 마스터 그리드 */}
        <div style={{ flex: '0 0 450px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <h4>Master - Lot List</h4>
          {loading && <div className="loading-overlay"></div>}
          <div style={{ flex: 1 }}>
            <DataGrid
              ref={masterGridRef}
              dataSource={masterData}
              keyExpr="id"
              height="100%"
              focusedRowEnabled={true}
              onFocusedRowChanged={handleFocusedRowChanged}
              onContentReady={handleMasterGridContentReady} // ★★★ 이벤트 핸들러 연결
              showBorders={true}
            >
              <Selection mode="single" />
              <Paging defaultPageSize={10} />
              <FilterRow visible={true} />   
              <Sorting mode="multiple" />             
              <Column dataField="FACTORY" caption="Factory" width={80} />
              <Column dataField="LOT_ID" caption="Lot ID" width={150} />
              <Column dataField="SHIPDATE" caption="Ship Date" />
            </DataGrid>
          </div>
        </div>

        {/* 디테일 그리드 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <h4>Sub - Lot History</h4>
          {subLoading && <div className="loading-overlay"></div>}
          <div style={{ flex: 1 }}>
            <DataGrid
              dataSource={subData}
              keyExpr="id"
              height="100%"
              showBorders={true}
              columnAutoWidth={true}
            >
              <Paging defaultPageSize={10} />
              <FilterRow visible={true} />
              <SearchPanel visible={true} width={240} />
              <HeaderFilter visible={true} />
              <Sorting mode="multiple" />   
              <Column dataField="HIST_SEQ" caption="Seq" width={80} fixed={true} />
              <Column dataField="LOT_ID" caption="LOT_ID" width={150} />
              <Column dataField="TRAN_CODE" caption="Trans Code" width={120} />
              <Column dataField="DEVICE" caption="Device" width={150} />
              <Column dataField="LOT_DESC" caption="PGM" width={250} />
              
              <Column dataField="TRAN_TIME" caption="Trans Time" width={180} />
              <Column dataField="OPER_FROM" caption="From" width={100} />
              <Column dataField="OPER_TO" caption="To" width={100} />
              <Column dataField="QTY_FROM" caption="Qty From" dataType="number" alignment="right" />
              <Column dataField="QTY_TO" caption="Qty To" dataType="number" alignment="right" />
              <Column dataField="LOSS_QTY" caption="Loss" dataType="number" alignment="right" />
              <Column dataField="CV_QTY" caption="CV_QTY" dataType="number" alignment="right" />
              
              <Column dataField="CON" caption="CON" width={250} />
              <Column dataField="SPLIT_IN" caption="SPLIT_IN" width={250} />
              <Column dataField="SPLIT_OUT" caption="SPLIT_OUT" width={250} />
              <Column dataField="SPLIT_LOT_ID" caption="SPLIT_LOT_ID" width={250} />
              <Column dataField="MERGE_IN" caption="MERGE_IN" width={250} />
              <Column dataField="MERGE_OUT" caption="MERGE_OUT" width={250} />

              <Column dataField="MERGE_LOT_ID" caption="MERGE_LOT_ID" width={250} />
              <Column dataField="CUSTOMER" caption="CUSTOMER" width={250} />
              <Column dataField="HOLD_CODE" caption="HOLD_CODE" width={250} />
              <Column dataField="PKG_FROM" caption="PKG_FROM" width={250} />          
              <Column dataField="PKG_TO" caption="PKG_TO" width={250} />
              <Column dataField="LEAD_FROM" caption="LEAD_FROM" width={250} />
              <Column dataField="LEAD_TO" caption="LEAD_TO" width={250} />
              <Column dataField="RES_ID" caption="TESTER ID" width={250} />              
              <Column dataField="HIST_DEL_FLAG" caption="HIST_DEL_FLAG" width={100} />
              <Column dataField="HIST_DEL_TIME" caption="HIST_DEL_TIME" width={100} />
              <Column dataField="CREATE_TIME" caption="CREATE_TIME" width={100} />
              <Column dataField="TRAN_USER_ID" caption="TRAN_USER_ID" width={100} />
            </DataGrid>
          </div>
        </div>
      </div>
    </div>
  );
}
